import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Space, Button, Card, Breadcrumb, Spin, Tag, Alert } from 'antd';
import { DeleteOutlined, InfoCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import noticeService from '../services/notices';
import { showConfirmModal } from '../components/PopupModal';
import authService from '../services/auth';
import NoticePreviewModal from '../components/NoticePreviewModal';
import {
  NoticeItem,
  getLayoutDisplayText,
  getExposureDisplayText,
  getApprovalStatusDisplayText
} from '../types/notice';
import { message } from '../utils/message';

const { Title, Text } = Typography;

const NoticeDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<NoticeItem | null>(null);
  const [approvalStatus, setApprovalStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [previewVisible, setPreviewVisible] = useState(false);

  // 안전한 날짜 포맷팅 함수
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('ko-KR');
    } catch {
      return '-';
    }
  };

  // 상시 노출 여부 확인 함수 (endDate가 50년 이상 미래이면 상시 노출로 판단)
  const isAlwaysVisible = (endDate: string | null | undefined): boolean => {
    if (!endDate) return false;
    try {
      const end = new Date(endDate);
      const now = new Date();
      const fiftyYearsFromNow = new Date();
      fiftyYearsFromNow.setFullYear(now.getFullYear() + 50);
      return end >= fiftyYearsFromNow;
    } catch {
      return false;
    }
  };

  const loadNoticeDetail = useCallback(async (noticeId: number) => {
    try {
      setLoading(true);
      const noticeData = await noticeService.getNoticeDetail(noticeId);
      setNotice(noticeData);
      setApprovalStatus(noticeData.approvalStatus || 'pending');
      setRejectionReason(noticeData.rejectionReason || '');
    } catch (error) {
      console.error('공지사항 상세 조회 실패:', error);
      message.error('공지사항 상세 정보를 불러오는데 실패했습니다.');
      navigate('/notices');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (id) {
      loadNoticeDetail(Number(id));
    }
  }, [id, loadNoticeDetail]);

  const handleEdit = () => {
    navigate(`/notices/edit/${id}`);
  };

  const handleDelete = async () => {
    showConfirmModal({
      title: '공지사항 삭제',
      content: `"${notice?.title || '해당 공지사항'}"을 삭제하시겠습니까?`,
      okText: '삭제',
      okType: 'danger',
      onConfirm: async () => {
        try {
          await noticeService.deleteNotice(Number(id));
          message.success('삭제가 완료되었습니다.');
          navigate('/notices');
        } catch (error) {
          console.error('Delete notice error:', error);
          message.error('공지사항 삭제에 실패했습니다.');
        }
      },
    });
  };

  const renderApprovalAlert = () => {
    let alertMessage = '';
    let alertType: 'info' | 'warning' | 'error' = 'info';
    let icon = <InfoCircleOutlined />;

    const isSuperAdmin = authService.isSuperAdmin();

    switch (approvalStatus) {
      case 'pending':
        if (isSuperAdmin) {
          alertMessage = '승인이 필요한 콘텐츠입니다. 검토 후 승인 여부를 진행해주세요.';
          alertType = 'info';
        } else {
          alertMessage = '승인 대기중인 콘텐츠입니다. 수정 및 삭제가 불가합니다.';
          alertType = 'warning';
        }
        icon = <InfoCircleOutlined />;
        break;
      case 'approved':
        alertMessage = '승인 완료된 콘텐츠입니다.';
        alertType = 'info';
        icon = <InfoCircleOutlined />;
        break;
      case 'rejected':
        alertMessage = rejectionReason
          ? `승인 거부된 콘텐츠입니다.\n사유 : ${rejectionReason}`
          : '승인 거부된 콘텐츠입니다.';
        alertType = 'error';
        icon = <CloseCircleOutlined />;
        break;
    }

    return (
      <Alert
        message={alertMessage}
        type={alertType}
        icon={icon}
        showIcon
        style={{ whiteSpace: 'pre-line', marginBottom: '24px' }}
      />
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!notice) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>공지사항을 찾을 수 없습니다.</Title>
        <Button onClick={() => navigate('/notices')}>목록으로 돌아가기</Button>
      </div>
    );
  }

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 브레드크럼 */}
        <Breadcrumb
          items={[
            { title: '콘텐츠 관리' },
            { title: '건물 안내' },
            { title: '공지사항', href: '/notices' },
            { title: '공지사항 상세' }
          ]}
          style={{ marginBottom: '8px' }}
        />

        {/* 페이지 제목 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2} style={{ margin: 0 }}>
            공지사항 상세
          </Title>
          <Button onClick={() => setPreviewVisible(true)}>미리보기</Button>
        </div>

        {renderApprovalAlert()}

        {/* 기본 정보 */}
        <Card title="기본 정보" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '16px' }}>
            <div>
              <Text style={{ fontWeight: 'bold', color: '#a6a6a6', display: 'block', marginBottom: '4px' }}>레이아웃</Text>
              <Text>{getLayoutDisplayText(notice.layout)}</Text>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '16px' }}>
            <div>
              <Text style={{ fontWeight: 'bold', color: '#a6a6a6', display: 'block', marginBottom: '4px' }}>노출여부</Text>
              <Text>{getExposureDisplayText(notice.exposure)}</Text>
            </div>
          </div>
          <div>
            <Text style={{ fontWeight: 'bold', color: '#a6a6a6', display: 'block', marginBottom: '4px' }}>노출기간</Text>
            <Text>
              {isAlwaysVisible(notice.endDate)
                ? '상시 노출'
                : `${new Date(notice.startDate).toLocaleDateString('ko-KR')} ~ ${new Date(notice.endDate).toLocaleDateString('ko-KR')}`
              }
            </Text>
          </div>
        </Card>

        {/* 상세 내용 */}
        <Card title="상세 내용" style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '24px' }}>
            <Text style={{ fontWeight: 'bold', color: '#a6a6a6', display: 'block', marginBottom: '4px' }}>제목</Text>
            <Text style={{ fontSize: '16px', fontWeight: '500' }}>{notice.title}</Text>
          </div>
          <div>
            <Text style={{ fontWeight: 'bold', color: '#a6a6a6', display: 'block', marginBottom: '12px' }}>내용</Text>
            {notice.contents && Array.isArray(notice.contents) ? notice.contents.map((content, index) => (
              <div key={index} style={{ marginBottom: index < notice.contents.length - 1 ? '16px' : 0, padding: '16px', background: '#1a1a1a', borderRadius: '8px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <Text style={{ fontWeight: '500', color: '#ffffff' }}>{content.subtitle || '제목 없음'}</Text>
                </div>
                <Text style={{ color: '#d9d9d9', lineHeight: '1.6', whiteSpace: 'pre-line' }}>{content.detail || '내용 없음'}</Text>
              </div>
            )) : (
              <div style={{ padding: '16px', background: '#1a1a1a', borderRadius: '8px' }}>
                <Text style={{ color: '#d9d9d9' }}>공지사항 내용이 없습니다.</Text>
              </div>
            )}
          </div>
        </Card>

        {/* 기타 정보 */}
        {notice.additionalInfo && (
          <Card title="기타 정보" style={{ marginBottom: '24px' }}>
            <div>
              <Text style={{ fontWeight: 'bold', color: '#a6a6a6', display: 'block', marginBottom: '4px' }}>추가 안내</Text>
              <Text style={{ lineHeight: '1.6', whiteSpace: 'pre-line' }}>{notice.additionalInfo}</Text>
            </div>
          </Card>
        )}

        {/* 승인 정보 */}
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>승인 정보</span>
            </div>
          }
          style={{ marginBottom: '32px' }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', paddingBottom: '24px' }}>
            <div>
              <Text style={{ fontWeight: 'bold', color: '#a6a6a6', display: 'block', marginBottom: '4px' }}>등록일</Text>
              <Text>{formatDate((notice as any).createdAt || (notice as any).created_at)}</Text>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', paddingBottom: '24px' }}>
            <div>
              <Text style={{ fontWeight: 'bold', color: '#a6a6a6', display: 'block', marginBottom: '4px' }}>승인일</Text>
              <Text>{formatDate(notice.approvedAt)}</Text>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
            <div>
              <Text style={{ fontWeight: 'bold', color: '#a6a6a6', display: 'block', marginBottom: '4px' }}>최근 수정일</Text>
              <Text>{
                ((notice as any).updatedAt || (notice as any).updated_at) !== ((notice as any).createdAt || (notice as any).created_at)
                  ? formatDate((notice as any).updatedAt || (notice as any).updated_at)
                  : '-'
              }</Text>
            </div>
          </div>
        </Card>

        {/* 액션 버튼 */}
        {notice.approvalStatus !== 'pending' && (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <Button
              danger
              size="large"
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              disabled={loading}
            >
              삭제
            </Button>
            {notice.approvalStatus === 'approved' && (
              <Button type="primary" size="large" onClick={handleEdit} disabled={loading}>
                수정
              </Button>
            )}
          </div>
        )}

        {/* 미리보기 모달 */}
        <NoticePreviewModal
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          notice={notice}
        />
      </Space>
    </Spin>
  );
};

export default NoticeDetail;